import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  NgZone,
  ElementRef,
  inject,
  DestroyRef,
  input,
  viewChild,
  output,
} from '@angular/core';
import { FieldType } from '@app/core/interfaces/table.interface';
import { Ag1TableService } from '../../ag1-table.service';
import { EMPTY } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  take,
} from 'rxjs/operators';
import { Ag1TableScrollSyncDirective } from '../../directives/ag1-table-scroll-sync.directive';
import { captureException } from '@sentry/angular';
import { MessageService } from '@openng/optimus-ui/api';
import {
  CdkVirtualScrollViewport,
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
} from '@angular/cdk/scrolling';
import { UUIDService } from '@app/core/services/uuid/uuid.service';
import {
  CdkDragDrop,
  moveItemInArray,
  CdkDropList,
  CdkDrag,
  CdkDragPlaceholder,
} from '@angular/cdk/drag-drop';
import { NgTemplateOutlet, NgPlural, NgPluralCase } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Bind } from '@openng/optimus-ui/bind';
import { Button } from '@openng/optimus-ui/button';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { Ag1LoaderComponent } from '../../../ag1-loader/ag1-loader.component';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { SentenceCasePipe } from '../../../../pipes/sentence.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface IColumnResizeState {
  isResizing: boolean;
  columnName: string | null;
  lastUpdate: number;
}

export interface IData {
  normalRows: INormalRow[];
  groupRows: IGroupRow[];
  virtualRows: (INormalRow | IGroupRow)[];
}

interface IGroupRow {
  groupRows: INormalRow[];
  groupFields: string[];
  groupValue: string;
  expanded: boolean;
  selected: boolean;
  count: number;
}

export interface INormalRow {
  id: number | string; // data must have an id
  [key: string]: any;
}

interface IRowExpansionState {
  expandedRowIndex: number | null;
  expandedRowGroupHeader: string | null;
}

@Component({
  selector: 'ag1-table-base',
  templateUrl: './ag1-table-base.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: ResizeObserver,
      useValue: typeof window !== 'undefined' ? window.ResizeObserver : null,
    },
  ],
  imports: [
    NgTemplateOutlet,
    Ag1TableScrollSyncDirective,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    AngularSvgIconModule,
    NgPlural,
    NgPluralCase,
    Bind,
    Button,
    Tooltip,
    Ag1LoaderComponent,
    HumanizePipe,
    SentenceCasePipe,
  ],
})
export class Ag1TableBaseComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private _messageService = inject(MessageService);
  private resizeObserver = inject(ResizeObserver);
  private uuidService = inject(UUIDService);

  // state
  fields: FieldType[];
  isLoading: boolean;
  data: IData = {
    normalRows: [],
    groupRows: [],
    virtualRows: [],
  };

  /** (Optional) Disable the loading animations on the table */
  readonly hasLoadingAnimation = input<boolean>(true);
  // (Optional) Disable the border around the table. Useful if the table is embedded inside a view
  readonly hasBorder = input<boolean>(true);
  // (Optional) Disable the border radius around the table. Useful if the table is embedded inside a view
  readonly isRounded = input<boolean>(true);
  /** (Optional) Whether to reset scroll position when data changes. Defaults to true. */
  readonly resetScrollOnDataChange = input<boolean>(true);
  /** (Optional) Default scroll behavior when scrolling to items. Defaults to 'center'. */
  readonly defaultScrollBehavior = input<'center' | 'top'>('center');

  // column dragging
  isDraggingDisabled: boolean = false;
  // Track if a column is being dragged
  // necessary to prevent tooltips from remaining open after a drop event
  isColumnDragging: boolean = false;

  // column resizing
  columnResizeState: IColumnResizeState = {
    isResizing: false,
    columnName: null,
    lastUpdate: 0,
  };

  // column sorting
  /** (Required) Event triggered when sorting a column. The purpose of this is to allow a feature to trigger sorting (locally or serverside) */
  readonly columnSortEvent = output<FieldType>();

  // nested templates
  /** (Optional) Pass in a template that'll be embedded in a nested row. This can be used to nest tables inside of Ag1TableBase */
  readonly nestedRowRef = input<TemplateRef<any>>(undefined);
  /** (Optional) Event triggered when expanding a nested row. The purpose of this is to allow a feature to capture which row was expanded in order to fetch new data for the nested table */
  readonly rowExpansionEvent = output<INormalRow>();
  rowExpansionState: IRowExpansionState = {
    expandedRowIndex: null,
    expandedRowGroupHeader: null,
  };

  // custom templates
  /** Pass in a template to customize a header cell */
  readonly customHeaderCellRef = input<TemplateRef<any>>(undefined);
  /** Pass in a template to customize a body cell */
  readonly customBodyCellRef = input<TemplateRef<any>>(undefined);
  /** Pass in a template to customize the action header cell */
  readonly customHeaderActionRef = input<TemplateRef<any>>(undefined);
  /** Pass in a template to customize the action body cell */
  readonly customBodyActionRef = input<TemplateRef<any>>(undefined);

  // data handling
  /** (Required) Service that contains the state of the Ag1TableBase components */
  readonly tableService = input.required<Ag1TableService>();
  /** (Required) Name of the table. This is used for data downloading */
  readonly tableName = input.required<string>();

  /** (Optional) The number of rows to display before virtual scrolling is enabled. */
  readonly virtualScrollCutoff = input<number>(undefined);
  /** (Optional) The height of each row in pixels. Required for virtual scrolling calculations. */
  readonly rowHeight = input<number>(45);

  // Future feature, need to give more thought into the ux of multi column sorting
  // For now it's disabled but can easily be enabled by setting this to true via some condition
  allowMultiColumnSort: boolean = false;

  // grouping
  expandedGroups: Set<string> = new Set<string>();

  // flashing
  flashedRows: Set<number | string> = new Set<number | string>();

  // selections
  selectedRows: Set<number | string> = new Set<number | string>();
  selectedGroups: Set<string> = new Set<string>();
  /** (Optional) Force selections to make use of role permissions. Disabled if no permission */
  readonly hasSelectionPermission = input<boolean>(true);

  /** Pass in a template to add bulk actions, this activates row selections */
  readonly customHeaderBulkActionRef = input<TemplateRef<any>>(undefined);
  /** (Optional) Event triggered when a row is selected */
  readonly rowSelectionEvent = output<(string | number)[]>();
  /** (Optional) Rows that should be excluded from selection */
  readonly excludedRowsFromSelection = input<(string | number)[]>([]);

  readonly scrollSyncDirective = viewChild(Ag1TableScrollSyncDirective);

  readonly viewport = viewChild(CdkVirtualScrollViewport);

  // Action Alignment
  // check the width of the header action cell and body action cell
  // set the width of both to the largest width
  readonly headerActionCell = viewChild<ElementRef>('headerActionCell');

  readonly bodyActionCell = viewChild<ElementRef>('bodyActionCell');

  // For nested tables, the nested table needs to match the width of the parent table
  // This is to ensure that scrolling is applied to the correct section (parent or nested table)
  // To achieve this we watch for changes in the parent table's width and update the nested table's width
  // which ensures that the nested table fits perfectly within the parent table which creates a good user experience
  readonly table = viewChild<ElementRef>('table');
  private resizeObserverSubscription?: any;
  parentWidth: number;

  checkedWidth: boolean = false;
  actionColumnWidth: number;
  viewportHeight: string;

  // Add new property to track content-based widths
  private contentBasedWidths: Map<string, number> = new Map();

  // CDK Drag and Drop
  /** (Optional) Connects this table's column drag-and-drop to external drop zones. */
  readonly connectedDropLists = input<(CdkDropList | string)[]>([]);
  /** (Optional) Emits when a column is dropped into an external zone. */
  readonly columnDroppedExternal = output<CdkDragDrop<FieldType[]>>();

  // Track by function for virtual scrolling
  trackByVirtualRow = (index: number, item: INormalRow | IGroupRow): any => {
    if (this.isNormalRow(item)) {
      return (item as INormalRow).id;
    } else {
      return (item as IGroupRow).groupValue;
    }
  };

  constructor() {}

  ngOnInit(): void {
    this.tableService()
      .isLoading$.pipe(
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged(),
        debounceTime(50),
        switchMap((isLoading: boolean) => {
          this.isLoading = isLoading;

          // start listening to data$ observable when isLoading$ emits a value
          return this.tableService().data$.pipe(
            takeUntilDestroyed(this._destroyRef),
            switchMap((data: any[]) => {
              // validate the data to ensure it has an id
              this.data.normalRows = this.mapDataToNormalRow(data);

              // Reset scroll position when data changes (if enabled)
              if (this.resetScrollOnDataChange()) {
                this.resetScrollPosition();
              }

              // start listening to fields$ observable when data$ emits a value
              return this.tableService().fields$.pipe(
                takeUntilDestroyed(this._destroyRef),
                map((fields: FieldType[]) => {
                  this.fields = fields;

                  // group the data and create virtual rows
                  this.generateVirtualRows();
                }),
              );
            }),
          );
        }),
        catchError((err) => {
          console.error(err);
          captureException(err);
          this._messageService.add({
            severity: 'error',
            summary: 'Unable to load table data',
          });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private mapDataToNormalRow(data: any): INormalRow[] {
    return data.map((item: any) => ({
      ...item,
      // all data must have an id in order for the table to work correctly
      // since we often use the table for data creation, items may not have an id
      // in such cases, we generate a unique id
      id: item.id || this.uuidService.generate(),
    }));
  }

  private alignActionColumns(): void {
    const headerActionCell: ElementRef = this.headerActionCell();
    const bodyActionCell: ElementRef = this.bodyActionCell();

    if (!headerActionCell || !bodyActionCell) {
      this.checkedWidth = false;
      return;
    }

    // prevent recalculating the width if it has already been calculated
    if (this.checkedWidth) {
      return;
    }

    // reset the width to get the actual width
    headerActionCell.nativeElement.style.width = '';
    bodyActionCell.nativeElement.style.width = '';

    // Get the computed widths
    const headerActionWidth =
      headerActionCell?.nativeElement?.getBoundingClientRect()?.width || 0;
    const bodyActionWidth =
      bodyActionCell?.nativeElement?.getBoundingClientRect()?.width || 0;

    // Set both columns to the maximum width
    const newWidth = Math.max(headerActionWidth, bodyActionWidth);
    this.actionColumnWidth = newWidth;

    // ensure that the width isn't recalculated
    this.checkedWidth = true;
  }

  getActionWidth(): string {
    return this.checkedWidth ? `${this.actionColumnWidth}px` : 'auto';
  }

  // Public method to manually trigger realignment
  public refreshActionColumnAlignment(): void {
    setTimeout(() => {
      this.alignActionColumns();
      this.refreshInterface();
    }, 20);
  }

  private resetScrollPosition(): void {
    const viewport: CdkVirtualScrollViewport = this.viewport();

    if (viewport) {
      viewport.scrollToIndex(0);
    }
  }

  /**
   * Scroll to a specific item index
   * @param index The index to scroll to
   * @param behavior Scroll behavior - 'center' or 'top'. If not provided, uses defaultScrollBehavior.
   */
  public scrollToIndex(index: number, behavior?: 'center' | 'top'): void {
    if (this.viewport() && index >= 0) {
      const scrollBehavior: 'center' | 'top' =
        behavior || this.defaultScrollBehavior();

      // Use a small timeout to ensure the viewport has updated with new data
      setTimeout(() => {
        if (scrollBehavior === 'center') {
          // Calculate offset to center the item in the viewport
          const viewportHeight: number = this.viewport().getViewportSize();
          const itemHeight: number = this.rowHeight();
          const itemOffset: number = index * itemHeight;
          const centerOffset: number =
            itemOffset - viewportHeight / 2 + itemHeight / 2;

          // Scroll to the calculated offset to center the item
          this.viewport().scrollToOffset(Math.max(0, centerOffset), 'smooth');
        } else {
          // 'top' - use CDK's default scrollToIndex (brings item to top/visible)
          this.viewport().scrollToIndex(index, 'smooth');
        }
      }, 50);

      this.flashBackground(index);
    }
  }

  /**
   * Temporarily change the background color applied to the row at the given index
   * @param index The index of the row to flash
   */
  flashBackground(index: number): void {
    const row: INormalRow = this.data.virtualRows[index] as INormalRow;
    if (row) {
      this.flashedRows.add(row.id);
      this.refreshInterface();

      setTimeout(() => {
        this.flashedRows.delete(row.id);
        this.refreshInterface();
      }, 750);
    }
  }

  /**
   * Scroll to center a specific item in the viewport
   * @param index The index to scroll to and center
   */
  public scrollToCenter(index: number): void {
    this.scrollToIndex(index, 'center');
  }

  /**
   * Scroll to bring a specific item to the top of the viewport
   * @param index The index to scroll to
   */
  public scrollToTop(index: number): void {
    this.scrollToIndex(index, 'top');
  }

  private refreshVirtualScroll(): void {
    const viewport: CdkVirtualScrollViewport = this.viewport();
    if (viewport) {
      // Force the virtual scroll to check for data changes
      viewport.checkViewportSize();
      // Trigger change detection to ensure the viewport renders new items
      this.cdr.detectChanges();
    }
  }

  refreshInterface(): void {
    this.cdr.detectChanges();
  }

  // data handling
  private generateVirtualRows(): void {
    if (!this.fields || !this.data.normalRows) {
      return;
    }

    // Calculate content-based widths before generating rows
    this.calculateContentBasedWidths();

    this.data.groupRows = this.groupedData();
    this.data.virtualRows = this.rowsToDisplay();
    this.setViewportHeight();

    // uncomment this for debuggging
    // console.log('table data', this.data);

    this.refreshInterface();
    this.refreshActionColumnAlignment();
    this.refreshVirtualScroll();

    // watch for size changes
    this.setupResizeObserver();
  }

  private calculateContentBasedWidths(): void {
    const measureElement = document.createElement('div');
    measureElement.style.position = 'absolute';
    measureElement.style.visibility = 'hidden';
    measureElement.style.whiteSpace = 'nowrap';
    measureElement.style.fontFamily = getComputedStyle(
      document.body,
    ).fontFamily;
    document.body.appendChild(measureElement);

    this.fields.forEach((field) => {
      // Measure header width
      measureElement.textContent = field.displayName || field.name;
      // Add extra padding for header (sort icons, resize handle, etc.)
      let maxWidth = measureElement.offsetWidth + 80; // Increased padding

      // Measure cell contents
      const sampleSize = Math.min(100, this.data.normalRows.length);
      for (let i = 0; i < sampleSize; i++) {
        const row = this.data.normalRows[i];
        const content = row[field.name];
        // Handle different content types
        measureElement.textContent = this.formatCellContent(content);
        const cellWidth = measureElement.offsetWidth + 40; // Add padding for cell content
        maxWidth = Math.max(maxWidth, cellWidth);
      }

      // Set minimum width (increased from 50)
      maxWidth = Math.max(maxWidth, 100);
      // Set maximum width (optional, increased from 500)
      maxWidth = Math.min(maxWidth, 800);

      // Store the calculated width
      this.contentBasedWidths.set(field.name, maxWidth);
      // Only set initial width if not already set by user
      if (!field.width) {
        field.width = maxWidth;
      }
    });

    document.body.removeChild(measureElement);
  }

  // Add helper method to format cell content
  private formatCellContent(content: any): string {
    if (content === null || content === undefined) {
      return '';
    }
    if (typeof content === 'object') {
      return JSON.stringify(content);
    }
    return String(content);
  }

  private setupResizeObserver(): void {
    const table: ElementRef = this.table();

    if (!table?.nativeElement) {
      return;
    }

    // Clean up any existing observer
    if (this.resizeObserverSubscription) {
      this.resizeObserverSubscription.disconnect();
    }

    // Create new observer with callback
    this.resizeObserverSubscription = new ResizeObserver(
      (_entries: ResizeObserverEntry[]) => {
        // Performance optimisation: Run outside Angular zone for better performance
        this.ngZone.runOutsideAngular(() => {
          // Performance optimisation: Schedule visual updates to sync with browser's refresh rate
          requestAnimationFrame(() => {
            // Return to Angular zone for the actual update
            this.ngZone.run(() => {
              // Force recalculation of parentWidth
              const newWidth: number =
                this.viewport()?.elementRef.nativeElement?.offsetWidth || 0;
              const newWidthWithoutPadding: number = newWidth - 16;

              // Only trigger change detection if the width actually changed
              if (this.parentWidth !== newWidthWithoutPadding) {
                this.parentWidth = newWidthWithoutPadding;
                this.refreshInterface();
              }
            });
          });
        });
      },
    );

    // Start observing
    this.resizeObserverSubscription.observe(table.nativeElement);
  }

  private rowsToDisplay(): (INormalRow | IGroupRow)[] {
    const rowsToShow: (INormalRow | IGroupRow)[] = [];

    if (this.hasGroupedData) {
      this.data.groupRows.forEach((groupRow: IGroupRow) => {
        rowsToShow.push(groupRow);

        if (groupRow.expanded) {
          rowsToShow.push(...groupRow.groupRows);
        }
      });
    } else {
      rowsToShow.push(...this.data.normalRows);
    }

    return rowsToShow;
  }

  isGroupRow(row: INormalRow | IGroupRow): boolean {
    return !(row as INormalRow)?.id && row.groupValue !== '';
  }

  isNormalRow(row: INormalRow | IGroupRow): boolean {
    // if the row has an id, it's a normal row
    return !!(row as INormalRow)?.id;
  }

  // nesting
  toggleNestedRow(
    item: INormalRow,
    rowIndex: number,
    groupHeader: string,
  ): void {
    if (this.rowExpansionState.expandedRowIndex === rowIndex) {
      this.collapseNestedRow();
    } else {
      this.expandNestedRow(item, rowIndex, groupHeader);
    }

    this.refreshInterface();
  }

  collapseNestedRow(): void {
    this.rowExpansionState = {
      expandedRowIndex: null,
      expandedRowGroupHeader: null,
    };
  }

  private expandNestedRow(
    item: INormalRow,
    rowIndex: number,
    groupHeader: string,
  ): void {
    this.rowExpansionState = {
      expandedRowIndex: rowIndex,
      expandedRowGroupHeader: groupHeader,
    };

    this.rowExpansionEvent.emit(item); // tell parent row was expanded
  }

  // grouping
  expandGroup(group: IGroupRow): void {
    group.expanded = true;
    this.expandedGroups.add(group.groupValue);

    this.generateVirtualRows();
  }

  collapseGroup(group: IGroupRow): void {
    group.expanded = false;
    this.expandedGroups.delete(group.groupValue);

    this.generateVirtualRows();
  }

  expandAllGroups(): void {
    this.data.groupRows.forEach((group) => {
      group.expanded = true;
      this.expandedGroups.add(group.groupValue);
    });

    this.generateVirtualRows();
  }

  collapseAllGroups(): void {
    this.data.groupRows.forEach((group) => {
      group.expanded = false;
      this.expandedGroups.delete(group.groupValue);
    });

    this.generateVirtualRows();
  }

  // selections
  get isAnyRowSelected(): boolean {
    return this.selectedRows.size > 0 && !this.isAllRowsSelected;
  }

  get isAllRowsSelected(): boolean {
    return this.selectedRows.size === this.data.normalRows.length;
  }

  isAnyGroupRowSelected(group: IGroupRow): boolean {
    return (
      group?.groupRows?.some((row: INormalRow) =>
        this.selectedRows.has(row.id),
      ) && !this.isAllGroupRowsSelected(group)
    );
  }

  isAllGroupRowsSelected(group: IGroupRow): boolean {
    return group?.groupRows?.every((row: INormalRow) =>
      this.selectedRows.has(row.id),
    );
  }

  toggleGroupSelection(group: IGroupRow): void {
    if (this.selectedGroups.has(group.groupValue)) {
      this.deselectGroup(group);
    } else {
      this.selectGroup(group);
    }
  }

  toggleRowSelection(row: INormalRow): void {
    if (this.excludedRowsFromSelection().includes(row.id)) {
      return;
    }

    if (this.selectedRows.has(row.id)) {
      this.deselectRow(row);
    } else {
      this.selectRow(row);
    }
  }

  toggleSelectAllRows(): void {
    if (this.isAllRowsSelected) {
      this.deselectAllRows();
    } else {
      this.selectAllRows();
    }
  }

  selectAllRows(): void {
    const selectedRows = this.data.normalRows.filter(
      (row: INormalRow) => !this.excludedRowsFromSelection().includes(row.id),
    );

    // prevent selecting an empty table/ groups
    if (selectedRows.length === 0) {
      return;
    }

    selectedRows.forEach((row: INormalRow) => this.selectedRows.add(row.id));

    this.data.groupRows.forEach((group: IGroupRow) =>
      this.selectedGroups.add(group.groupValue),
    );

    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  deselectAllRows(): void {
    this.selectedRows.clear();
    this.selectedGroups.clear();

    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  selectGroup(group: IGroupRow): void {
    const rowsToSelect = group.groupRows.filter(
      (row: INormalRow) => !this.excludedRowsFromSelection().includes(row.id),
    );

    // prevent selecting an empty group
    if (rowsToSelect.length === 0) {
      return;
    }

    this.selectedGroups.add(group.groupValue);

    rowsToSelect.forEach((row: INormalRow) => this.selectedRows.add(row.id));

    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  deselectGroup(group: IGroupRow): void {
    this.selectedGroups.delete(group.groupValue);
    group.groupRows.forEach((row: INormalRow) =>
      this.selectedRows.delete(row.id),
    );

    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  selectRow(row: INormalRow): void {
    if (this.excludedRowsFromSelection().includes(row.id)) {
      return;
    }

    this.selectedRows.add(row.id);

    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  deselectRow(row: INormalRow): void {
    this.selectedRows.delete(row.id);

    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  get hasSelectableData(): boolean {
    return !!this.customHeaderBulkActionRef() && this.hasSelectionPermission();
  }

  get isAllGroupsExpanded(): boolean {
    return this.data.groupRows.every((group: IGroupRow) => group.expanded);
  }

  get checkedFields(): FieldType[] {
    return (
      this.fields?.filter((field: FieldType) => field.checked) ?? []
    ).sort((a, b) => a.order - b.order);
  }

  get groupedFields(): FieldType[] {
    return this.fields
      .filter((field: FieldType) => field.grouped && field.groupable)
      .sort((a, b) => {
        if (a.groupOrder === null) return 1;
        if (b.groupOrder === null) return -1;
        return a.groupOrder - b.groupOrder;
      });
  }

  get groupedFieldKeys(): string[] {
    return this.groupedFields.map((field: FieldType) => field.name);
  }

  private groupedData(): IGroupRow[] {
    if (!this.data?.normalRows?.length) {
      return [];
    }

    const groupedData: IGroupRow[] = [];
    this.data.normalRows.forEach((item: INormalRow) => {
      const groupValue: string = this.groupedFieldKeys
        .map((field: string) =>
          item[field] === null ||
          item[field] === undefined ||
          item[field] === ''
            ? '(no value)'
            : item[field],
        )
        .join(' | ');

      const existingGroup: IGroupRow = groupedData.find(
        (group: IGroupRow) => group.groupValue === groupValue,
      );

      if (existingGroup) {
        existingGroup.groupRows.push(item);
        existingGroup.count++;
      } else {
        groupedData.push({
          groupValue,
          groupRows: [item],
          groupFields: this.groupedFieldKeys,
          expanded: this.expandedGroups.has(groupValue),
          selected: this.selectedGroups.has(groupValue),
          count: 1,
        });
      }
    });

    return groupedData;
  }

  get hasGroupedData(): boolean {
    return !!this.groupedFields.length;
  }

  get hasNestedRow(): boolean {
    return !!this.nestedRowRef();
  }

  get hasCustomAction(): boolean {
    return !!this.customBodyActionRef() || !!this.customHeaderActionRef();
  }

  resetSelectedRows(): void {
    this.selectedRows.clear();
    this.selectedGroups.clear();
  }

  selectRowById(id: number | string): void {
    if (this.excludedRowsFromSelection().includes(id)) {
      return;
    }

    // Find the row in the data - use take(1) to get current value from Observable
    this.tableService()
      .data$.pipe(take(1))
      .subscribe((data) => {
        if (!data?.length) {
          return;
        }

        // Data is a flat array, find the row by id
        const row = data.find((r: any) => String(r.id) === String(id));
        if (row) {
          this.selectedRows.add(id);
          this.rowSelectionEvent.emit(Array.from(this.selectedRows));
          this.cdr.detectChanges();
        }
      });
  }

  // column sorting
  updateColumnSortDirection(field: FieldType): void {
    if (!field?.sortable) {
      return;
    }

    this.tableService()
      .fields$.pipe(
        take(1),
        map((fields: FieldType[]) => {
          const updatedFields: FieldType[] = fields.map((f: FieldType) => {
            if (f.name === field.name) {
              return {
                ...f,
                sortDirection: this.tableService().sortDirection(f),
              };
            }

            return f;
          });

          // Get the sorted non-grouped fields
          const sortedNonGroupedFields: FieldType[] = updatedFields.filter(
            (f: FieldType) => !f.grouped && f.sortDirection,
          );

          if (sortedNonGroupedFields.length > 0) {
            // If there are sorted non-grouped fields, remove sortDirection from grouped fields
            updatedFields.forEach((f) => {
              if (f.grouped) {
                f.sortDirection = null;
              }

              if (
                f.name !== field.name &&
                f.sortDirection &&
                !this.allowMultiColumnSort
              ) {
                f.sortDirection = null;
              }
            });
          } else {
            // If there are no sorted non-grouped fields, sort the grouped fields
            updatedFields.forEach((f: FieldType) => {
              if (f.grouped && f.sortDirection === null) {
                f.sortDirection = this.tableService().sortDirection(f);
              }
            });
          }

          return updatedFields;
        }),
      )
      .subscribe((fields: FieldType[]) => {
        this.tableService().updateFields(fields);

        this.columnSortEvent.emit(field);
      });
  }

  onColumnDrop(event: CdkDragDrop<FieldType[]>): void {
    if (event.previousContainer === event.container) {
      const checked = [...this.checkedFields];
      moveItemInArray(checked, event.previousIndex, event.currentIndex);

      const unchecked = this.fields
        .filter((f) => !f.checked)
        .sort((a, b) => a.order - b.order);
      const merged = [...checked, ...unchecked];
      merged.forEach((field, index) => (field.order = index));

      this.tableService().updateFields(merged);
    }

    this.realignColumns();
  }

  onColumnDragStarted(): void {
    this.isColumnDragging = true;
  }

  onColumnDragEnded(): void {
    this.isColumnDragging = false;
  }

  // column resizing
  onColumnResizeMouseDown(event: MouseEvent, field: FieldType): void {
    event.preventDefault();

    this.isDraggingDisabled = true;

    // Initialize resize state
    this.columnResizeState.columnName = field.name;
    const initialMousePosition: number = event.clientX;
    let previousMousePosition: number = initialMousePosition;
    const initialWidth: number =
      field.width || this.contentBasedWidths.get(field.name) || 200;

    // Performance optimisation: Move column resize logic outside Angular's change detection zone.
    // This prevents unnecessary change detection cycles during resize
    // which drastically improves performance
    this.ngZone.runOutsideAngular(() => {
      const onMouseMove = (mouseMoveEvent: MouseEvent): void => {
        // Calculate horizontal distance moved since last mouse position
        const horizontalDelta: number =
          mouseMoveEvent.clientX - previousMousePosition;
        previousMousePosition = mouseMoveEvent.clientX;

        // Performance optimisation: Schedule visual updates to sync with browser's refresh rate
        // This ensures smooth animation by aligning with screen refresh
        // Performance is improved as the browser can batch updates
        // by grouping them together before the next repaint cycle
        requestAnimationFrame(() => {
          const newWidth = Math.max(
            100,
            initialWidth + (mouseMoveEvent.clientX - initialMousePosition),
          );
          field.width = newWidth;
          // Update the content-based width as well
          this.contentBasedWidths.set(field.name, newWidth);
          this.columnResizeState.isResizing = true;
          this.refreshInterface();
        });
      };

      const onMouseUp = (): void => {
        // Remove event listeners to prevent memory leaks
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);

        // Return to Angular zone to properly update application state
        // Which will trigger change detection and update the UI
        this.ngZone.run(() => {
          this.updateColumnWidth(field);
          this.isDraggingDisabled = false;
        });
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    });
  }

  private updateColumnWidth(field: FieldType): void {
    this.tableService()
      .fields$.pipe(
        take(1),
        map((fields: FieldType[]) => {
          const updatedFields: FieldType[] = fields.map((f: FieldType) =>
            f.name === field.name ? { ...f, width: f.width } : f,
          );

          return updatedFields;
        }),
      )
      .subscribe((fields: FieldType[]) => {
        this.resetColumnResizeState();
        this.tableService().updateFields(fields);
      });
  }

  private resetColumnResizeState(): void {
    this.columnResizeState = {
      isResizing: false,
      columnName: null,
      lastUpdate: 0,
    };
  }

  // Update getColumnWidth method
  getColumnWidth(field: FieldType): string {
    // Prioritize user-resized width over content-based width
    const width = field.width || this.contentBasedWidths.get(field.name) || 200;
    return `${width}px`;
  }

  private setViewportHeight(): void {
    // if the number of rows isn't specified, allow table to grow to the height of the parent container
    if (!this.virtualScrollCutoff()) {
      this.viewportHeight = '100%';
      return;
    }

    // determine the height
    this.viewportHeight = `${this.calculateViewPortHeightFromRows()}px`;
  }

  realignColumns(): void {
    this.scrollSyncDirective()?.forceSync();
  }

  private calculateViewPortHeightFromRows(): number {
    const numberOfRows: number = this.data.virtualRows.length;
    // need to account for scrollbar height to prevent scrollbar from covering the last row
    const scrollbarHeight: number = 16;
    const height: number =
      Math.min(numberOfRows, this.virtualScrollCutoff()) * this.rowHeight();

    return height + scrollbarHeight;
  }
}
